import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RelationshipProfilesCard = ({ relationships, onViewAll, onAddNew }) => {
  const getRelationshipIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'family':
        return 'Users';
      case 'friend':
        return 'UserCheck';
      case 'colleague':
        return 'Briefcase';
      case 'partner':
        return 'Heart';
      default:
        return 'User';
    }
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'text-green-600 bg-green-50';
    if (rate >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft border border-accent-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Icon name="Users" size={24} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-accent-800">Relationship Profiles</h3>
            <p className="text-sm text-accent-600">{relationships.length} people in your network</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="Plus"
            onClick={onAddNew}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          >
            Add New
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={onViewAll}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          >
            View All
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {relationships.slice(0, 4).map((person) => (
          <div key={person.id} className="p-5 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200 cursor-pointer border border-gray-200">
            <div className="flex items-center gap-4 mb-4">
              <Image
                src={person.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(person.name)}&background=f97316&color=ffffff&size=150`}
                alt={person.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-800 text-base truncate">{person.name}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <Icon name={getRelationshipIcon(person.relationship)} size={14} className="text-gray-500" />
                  <span className="text-sm text-gray-600">{person.relationship}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600 font-medium">Gift Success Rate</span>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getSuccessRateColor(person.successRate)}`}>
                {person.successRate}%
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
              <span className="font-medium">{person.giftsGiven} gifts given</span>
              <span>Last: {person.lastGift}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {person.interests.slice(0, 3).map((interest, index) => (
                <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium border border-blue-200">
                  {interest}
                </span>
              ))}
              {person.interests.length > 3 && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium border border-gray-200">
                  +{person.interests.length - 3}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {relationships.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Users" size={24} className="text-gray-400" />
          </div>
          <p className="text-gray-600 mb-2 font-medium">No relationship profiles yet</p>
          <p className="text-sm text-gray-500 mb-4">Add people to track their preferences and gift history</p>
          <Button
            variant="primary"
            size="sm"
            iconName="Plus"
            onClick={onAddNew}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Add Your First Person
          </Button>
        </div>
      )}
    </div>
  );
};

export default RelationshipProfilesCard;